import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, TrendingUp, Award, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

interface HazardCategory {
  category: string;
  count: number;
  percentage: number;
  label: string;
  icon: string;
}

const HAZARD_LABELS: Record<string, { label: string; icon: string }> = {
  no_ppe: { label: 'APD Tidak Lengkap', icon: '🥽' },
  unsafe_procedure: { label: 'Prosedur Tidak Aman', icon: '📋' },
  spill: { label: 'Tumpahan', icon: '💧' },
  slippery_floor: { label: 'Lantai Licin', icon: '⚠️' },
  equipment_fault: { label: 'Kerusakan Peralatan', icon: '🔧' },
  electrical_hazard: { label: 'Bahaya Listrik', icon: '⚡' },
  fire_hazard: { label: 'Bahaya Kebakaran', icon: '🔥' },
  chemical_hazard: { label: 'Bahaya Kimia', icon: '☣️' },
  fall_hazard: { label: 'Bahaya Jatuh', icon: '🪜' },
  housekeeping: { label: 'Housekeeping', icon: '🧹' },
  confined_space: { label: 'Ruang Terbatas', icon: '🚪' },
  welding: { label: 'Pengelasan', icon: '🔥' },
  machine_hazard: { label: 'Bahaya Mesin', icon: '⚙️' },
  improper_lifting: { label: 'Angkat Tidak Benar', icon: '📦' },
};

interface Props {
  workerId: string;
  onStartQuest?: (category: string) => void;
}

export function HazardDiversityTracker({ workerId, onStartQuest }: Props) {
  const [categories, setCategories] = useState<HazardCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [diversityScore, setDiversityScore] = useState(0);
  const [suggestedCategory, setSuggestedCategory] = useState<string | null>(null);

  useEffect(() => {
    loadReportStats();
  }, [workerId]);

  const loadReportStats = async () => {
    try {
      const { data: reports, error } = await supabase
        .from('peer_reports')
        .select('hazard_category')
        .eq('reporter_id', workerId);

      if (error) throw error;

      // Count categories
      const counts: Record<string, number> = {};
      reports?.forEach(r => {
        const cat = r.hazard_category || 'other';
        counts[cat] = (counts[cat] || 0) + 1;
      });

      const total = reports?.length || 0;
      const categorized = Object.entries(counts)
        .map(([category, count]) => ({
          category,
          count,
          percentage: total > 0 ? (count / total) * 100 : 0,
          label: HAZARD_LABELS[category]?.label || category.replace(/_/g, ' '),
          icon: HAZARD_LABELS[category]?.icon || '⚠️',
        }))
        .sort((a, b) => b.count - a.count);

      setCategories(categorized);

      // Calculate diversity score (higher is better, means more diverse reporting)
      const uniqueCategories = Object.keys(counts).length;
      const maxCategories = Object.keys(HAZARD_LABELS).length;
      const diversityPercent = Math.min(100, (uniqueCategories / Math.min(maxCategories, 8)) * 100);
      setDiversityScore(diversityPercent);

      // Find suggested category (one they haven't reported yet)
      const reportedCategories = new Set(Object.keys(counts));
      const unreportedCategories = Object.keys(HAZARD_LABELS).filter(c => !reportedCategories.has(c));
      if (unreportedCategories.length > 0) {
        setSuggestedCategory(unreportedCategories[Math.floor(Math.random() * unreportedCategories.length)]);
      }

    } catch (error) {
      console.error('Error loading report stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/2 mb-4"></div>
        <div className="space-y-2">
          <div className="h-3 bg-muted rounded"></div>
          <div className="h-3 bg-muted rounded w-3/4"></div>
        </div>
      </Card>
    );
  }

  const topCategory = categories[0];
  const isTooNarrow = topCategory && topCategory.percentage > 60;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          Diversitas Laporan
        </h3>
        <Badge variant={diversityScore >= 60 ? "default" : "destructive"} className={`text-xs ${diversityScore >= 60 ? 'bg-success text-success-foreground' : ''}`}>
          {Math.round(diversityScore)}%
        </Badge>
      </div>

      {/* Diversity Progress */}
      <div className="mb-4">
        <Progress value={diversityScore} className="h-2" />
        <p className="text-xs text-muted-foreground mt-1">
          {diversityScore >= 60 
            ? "Bagus! Anda mengidentifikasi berbagai jenis bahaya." 
            : "Coba identifikasi lebih banyak jenis bahaya."}
        </p>
      </div>

      {/* Category Breakdown */}
      {categories.length > 0 && (
        <div className="space-y-2 mb-4">
          {categories.slice(0, 4).map((cat, idx) => (
            <motion.div
              key={cat.category}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="flex items-center gap-2"
            >
              <span className="text-lg">{cat.icon}</span>
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm">
                  <span>{cat.label}</span>
                  <span className="font-medium">{cat.count}</span>
                </div>
                <Progress value={cat.percentage} className="h-1.5 mt-1" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Warning if too narrow */}
      {isTooNarrow && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-4"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                Laporan Terlalu Sempit
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {Math.round(topCategory.percentage)}% laporan Anda adalah "{topCategory.label}". 
                Coba perhatikan bahaya lain seperti {HAZARD_LABELS[suggestedCategory || 'spill']?.label}.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Suggested Quest */}
      {suggestedCategory && onStartQuest && (
        <Button 
          variant="outline" 
          className="w-full justify-between"
          onClick={() => onStartQuest(suggestedCategory)}
        >
          <div className="flex items-center gap-2">
            <span>{HAZARD_LABELS[suggestedCategory]?.icon}</span>
            <span className="text-sm">
              Quest: Temukan {HAZARD_LABELS[suggestedCategory]?.label}
            </span>
          </div>
          <div className="flex items-center gap-1 text-amber-500">
            <Award className="h-4 w-4" />
            <span className="text-xs font-medium">+15 XP</span>
            <ChevronRight className="h-4 w-4" />
          </div>
        </Button>
      )}

      {/* Achievement for diversity */}
      {diversityScore >= 80 && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="mt-4 p-3 rounded-lg bg-success/10 border border-success/20 flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-success" />
          </div>
          <div>
            <p className="text-sm font-medium text-success">Pengamat Handal!</p>
            <p className="text-xs text-muted-foreground">
              Anda mengidentifikasi banyak jenis bahaya
            </p>
          </div>
        </motion.div>
      )}
    </Card>
  );
}
